import React from 'react';
import { OibTextFieldProps } from './types';
import { isOibValid } from './validation';
import { TextField } from '../TextField';

/** OIB ima točno 11 znamenki. */
const OIB_LENGTH = 11;

/**
 * OIB je tekst od znamenki, ne broj: kao broj bi dobio točke za tisućice i izgubio vodeću
 * nulu (01234567890 bi postao 1234567890). Unos zato propušta samo znamenke, najviše 11.
 */
const OibTextField: React.FC<OibTextFieldProps> = ({
  value,
  onChange,
  error,
  validateOnLength = true,
  placeholder = 'Unesite OIB (11 znamenki)',
  name,
  ...props
}) => {
  const oib = typeof value === 'string' ? value : (value ? String(value) : '');

  const isValid = React.useMemo(() => {
    if (!oib) return true;
    if (oib.length !== OIB_LENGTH) return false;
    return isOibValid(oib);
  }, [oib]);

  const handleOibChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (!onChange) return;

    const digits = e.target.value.replace(/\D/g, '').slice(0, OIB_LENGTH);
    const event = {
      target: {
        name: e.target.name || name || '',
        value: digits,
      },
    } as React.ChangeEvent<HTMLInputElement>;
    onChange(event);
  }, [onChange, name]);

  const validationError = !isValid ? 'Neispravan OIB' : (!oib ? error : undefined);

  const digitCount = oib.length;
  const hasDigits = digitCount > 0;

  return (
    <div className="relative">
      {/* disableDebounce: polje odmah prikazuje očišćenu vrijednost, bez slova i viška znamenki. */}
      <TextField
        {...props}
        type="text"
        name={name}
        value={oib}
        onChange={handleOibChange}
        error={validationError}
        placeholder={placeholder}
        maxLength={OIB_LENGTH}
        disableDebounce
      />
      {hasDigits && (
        <div className="absolute -bottom-5 left-0 right-0 flex items-center justify-between px-1">
          <span className="text-xs text-slate-400">{digitCount}/{OIB_LENGTH} znamenki</span>
          {digitCount === OIB_LENGTH && (
            <span className={`text-xs font-medium ${isValid ? "text-green-600" : "text-red-500"}`}>
              {isValid ? "Ispravan OIB" : "Neispravan OIB"}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo(OibTextField);
